/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { Button } from 'src/components/core/buttons/button';
import { FadeInUp } from 'src/components/core/animations/fade-in-up';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { IconParallax } from 'src/components/core/animations/icon-parallax';
import { Image } from 'src/components/core/image';
import { Section } from 'src/components/core/layout/section';
import { breakpoints } from '@untile/react-core/styles/breakpoints';
import { media } from '@untile/react-core/styles/media';
import { motion, useInView } from 'framer-motion';
import { routeResolve } from 'src/core/utils/routes';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useRef } from 'react';
import { useTranslation } from 'next-i18next';
import carIcon from 'src/assets/svgs/car.svg';
import iconCalendar from 'src/assets/svgs/calendar.svg';
import iconRight from 'src/assets/svgs/arrow-right.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  iconsParallax?: boolean;
  labelAlternative?: boolean;
  titleAlternative?: boolean;
};

/*
 * `Content` styled component.
 */

const Content = styled(SubGrid)`
  ${media.min.md`
    display: grid;
    grid-template-columns: 1fr 1fr;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled(FadeInUp)`
  border-radius: var(--border-radius-sm);
  height: 308px;
  justify-self: end;
  margin-top: 10px;
  overflow: hidden;
  position: relative;
  width: 308px;
`;

/**
 * `IconsGrid` styled component.
 */

const IconsGrid = styled(motion.div)`
  bottom: 0;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;

  ${media.min.md`
    display: grid;
    grid-column-gap: var(--space-xxs);
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(6, 1fr);
  `}
`;

/**
 * Export `Schedule` component.
 */

export const Schedule = ({ iconsParallax = true, labelAlternative, titleAlternative }: Props) => {
  const { t } = useTranslation('common');
  const isMobile = useBreakpoint(0, 'md');

  const iconsGridRef = useRef(null);
  const isInView = useInView(iconsGridRef, { once: false });

  return (
    <AnimationController>
      <Section style={{ overflow: 'hidden' }}>
        <Grid>
          <Content>
            <div style={{ paddingInline: isMobile ? 'var(--space-sm)' : 0 }}>
              <Heading
                asTitle={'h2'}
                icon={iconCalendar}
                label={labelAlternative ? t('scheduleService.labelAlternative') : t('scheduleService.label')}
                style={{ marginBottom: 'var(--space-ms)' }}
                title={titleAlternative ? t('scheduleService.titleAlternative') : t('scheduleService.title')}
                titleSize={28}
              />

              <FadeInUp>
                <Button
                  aria-label={t('actions.schedule')}
                  href={routeResolve('schedule')}
                  icon={iconRight}
                  reverse
                  size={'medium'}
                >
                  {t('actions.schedule')}
                </Button>
              </FadeInUp>
            </div>

            {!isMobile && (
              <ImageWrapper>
                <Image
                  alt={t('scheduleService.image.alt')}
                  fill
                  sizes={`(max-width: ${breakpoints.xxl}px) 50vw, 50vw`}
                  src={t('scheduleService.image.src')}
                  style={{ objectFit: 'cover' }}
                />
              </ImageWrapper>
            )}
          </Content>
        </Grid>

        {!isMobile && iconsParallax && (
          <IconsGrid ref={iconsGridRef}>
            <IconParallax
              baseVelocity={0.2}
              icon={carIcon}
              isInView={isInView}
              name={t('scheduleService.icons.suspension')}
              style={{ gridColumn: 7, gridRow: 2, scale: 1.5 }}
            />

            <IconParallax
              baseVelocity={0.8}
              icon={carIcon}
              isInView={isInView}
              name={t('scheduleService.icons.eletric')}
              style={{ gridColumn: 8, gridRow: 5 }}
            />

            <IconParallax
              baseVelocity={1.5}
              icon={carIcon}
              isInView={isInView}
              name={t('scheduleService.icons.tires')}
              style={{ gridColumn: 10, gridRow: 6 }}
            />
          </IconsGrid>
        )}
      </Section>
    </AnimationController>
  );
};
